import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { termsService, Terms, CheckAcceptanceResponse } from '../services/terms.service';
import useAuth from './useAuth';

interface TermsContextType {
  needsAcceptance: boolean;
  terms: Terms | null;
  loading: boolean;
  acceptTerms: () => Promise<void>;
  recheckTerms: () => Promise<void>;
}

const TermsContext = createContext<TermsContextType>({} as TermsContextType);

export function useTerms() {
  return useContext(TermsContext);
}

export function TermsProvider({ children }: { children: React.ReactNode }) {
  const { userData } = useAuth();
  const [needsAcceptance, setNeedsAcceptance] = useState(false);
  const [terms, setTerms] = useState<Terms | null>(null);
  const [loading, setLoading] = useState(true);

  const checkTerms = useCallback(async () => {
    // Só verifica se o usuário está autenticado
    if (!userData?.isAuth) {
      setLoading(false);
      return;
    }

    try {
      const result: CheckAcceptanceResponse = await termsService.checkCustomerAcceptance();

      if (!result.hasAccepted && result.terms) {
        setTerms(result.terms);
        setNeedsAcceptance(true);
      } else {
        setNeedsAcceptance(false);
      }
    } catch (error) {
      console.error('Erro ao verificar termos:', error);
      // Se der erro, não bloqueia o usuário
      setNeedsAcceptance(false);
    } finally {
      setLoading(false);
    }
  }, [userData?.isAuth]);

  const acceptTerms = useCallback(async () => {
    if (!terms) return;

    try {
      await termsService.acceptTermsCustomer(terms.id);
      setNeedsAcceptance(false);
      setTerms(null);
    } catch (error) {
      console.error('Erro ao aceitar termos:', error);
      throw error;
    }
  }, [terms]);

  const recheckTerms = useCallback(async () => {
    setLoading(true);
    await checkTerms();
  }, [checkTerms]);

  useEffect(() => {
    checkTerms();
  }, [checkTerms]);

  return (
    <TermsContext.Provider
      value={{
        needsAcceptance,
        terms,
        loading,
        acceptTerms,
        recheckTerms,
      }}
    >
      {children}
    </TermsContext.Provider>
  );
}
